// src/views/admin/main/others/messages/Messages.js

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'contexts/authContext';
import { useChat } from 'hooks/useChat';
import { fetchUsersAssignedToMentor, fetchTeamsByMentor, fetchTeamMembers, fetchUserById } from 'api';
import Card from 'components/card';
import DottedLoader from 'components/loaders/DottedLoader';
import Chat from './components/Chat';
import SingleChat from './components/SingleChat';
import { BsPencilSquare, BsPersonFill, BsFillPeopleFill } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import { FaChevronLeft, FaChevronRight, FaComments } from "react-icons/fa";
import { FaUserFriends } from 'react-icons/fa'; // Added for mentor fallback icon
import { useLocation, useNavigate } from 'react-router-dom';

const MOBILE_BREAKPOINT = 1024;

const Messages = () => {
    const { user } = useAuth();
    const { chats, isChatsLoading } = useChat(user);
    const [selectedChat, setSelectedChat] = useState(null);
    const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState('teams');
    const [currentPage, setCurrentPage] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
    const [chatToGlowId, setChatToGlowId] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Dynamic contacts per page based on screen size
    const contactsPerPage = isMobile ? 6 : 7;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const chatWithId = queryParams.get("chatWith");
        const teamChatWithId = queryParams.get("teamChatWith");

        if (!chats || (!chatWithId && !teamChatWithId)) return;

        let chatToOpen = null;
        if (chatWithId) {
            chatToOpen = chats.find(c => c.type === 'individual' && c.participant?.id === parseInt(chatWithId));
        } else if (teamChatWithId) {
            chatToOpen = chats.find(c => c.type === 'team' && c.id === parseInt(teamChatWithId));
        }

        if (chatToOpen) {
            const uniqueId = `${chatToOpen.type}-${chatToOpen.id}`;
            setSelectedChat({ ...chatToOpen, uniqueId });
            setChatToGlowId(uniqueId);
            
            const timeout = setTimeout(() => {
                setChatToGlowId(null);
            }, 3000); 

            navigate(location.pathname, { replace: true });
            return () => clearTimeout(timeout);
        }
    }, [location.search, chats, navigate]);

    const { data: allContacts, isLoading: areContactsLoading } = useQuery({
        queryKey: ['allContacts', user?.id],
        queryFn: async () => {
            if (!user) return [];
            if (user.role === 'mentor') {
                const students = await fetchUsersAssignedToMentor(user.id);
                const teams = await fetchTeamsByMentor(user.id);
                const studentsWithTeams = students.map(student => ({
                    ...student,
                    team: teams.find(t => t.id === student.teamId) || null,
                    type: 'individual'
                }));
                return [
                    ...studentsWithTeams,
                    ...teams.map(t => ({ ...t, type: 'team' }))
                ];
            } else {
                // Allow users without teams to still see their mentor
                const contacts = [];
                
                // Add mentor if user has one, regardless of team status
                if (user.mentorId) {
                    try {
                        const mentor = await fetchUserById(user.mentorId);
                        if (mentor && mentor.firstName && mentor.lastName) {
                            contacts.push({ 
                                ...mentor, 
                                type: 'individual', 
                                isMentorChat: true,
                                // Ensure required fields are present
                                firstName: mentor.firstName || 'Mentor',
                                lastName: mentor.lastName || 'User',
                                email: mentor.email || '',
                                imageUrl: mentor.imageUrl || null,
                                status: mentor.status || 'Offline',
                                lastSeen: mentor.lastSeen || null
                            });
                        } else {
                            console.warn('Mentor data incomplete:', mentor);
                        }
                    } catch (error) {
                        console.error('Error fetching mentor:', error);
                    }
                }
                
                // Add teammates if user has a team
                if (user.teamId) {
                    try {
                        const teammates = await fetchTeamMembers(user.teamId);
                        const teammateContacts = teammates
                            .filter(t => t.id !== user.id && t.firstName && t.lastName)
                            .map(t => ({ ...t, type: 'individual' }));
                        contacts.push(...teammateContacts);
                    } catch (error) {
                        console.error('Error fetching teammates:', error);
                    }
                }
                
                return contacts;
            }
        },
        enabled: !!user && isCreatingNewChat,
    });

    const { teams, users: contactsForNewChat } = useMemo(() => {
        if (!allContacts) return { teams: [], users: [] };
        const lowerCaseSearch = searchTerm.toLowerCase();
        if (user?.role === 'mentor') {
            const teamsList = allContacts.filter(c => c.type === 'team' && c.name?.toLowerCase().includes(lowerCaseSearch));
            const usersList = allContacts.filter(c => c.type === 'individual' && (`${c.firstName} ${c.lastName}`).toLowerCase().includes(lowerCaseSearch));
            return { teams: teamsList, users: usersList };
        }
        return { teams: [], users: allContacts.filter(c => (`${c.firstName} ${c.lastName}`).toLowerCase().includes(lowerCaseSearch)) };
    }, [allContacts, searchTerm, user]);

    const activeContactList = useMemo(() => (
        user?.role === 'mentor' ? (activeTab === 'teams' ? teams : contactsForNewChat) : contactsForNewChat
    ), [user, activeTab, teams, contactsForNewChat]);

    // Pagination logic for new contacts - now uses dynamic contactsPerPage
    const totalPages = Math.ceil((activeContactList?.length || 0) / contactsPerPage);
    const paginatedContacts = useMemo(() => {
        if (!activeContactList) return [];
        const startIndex = currentPage * contactsPerPage;
        return activeContactList.slice(startIndex, startIndex + contactsPerPage);
    }, [activeContactList, currentPage, contactsPerPage]);

    // Filtered chats with search
    const filteredChats = useMemo(() => {
        if (!chats) return [];
        const filtered = chats.filter(chat =>
            (chat.type === 'team' && chat.team?.name) ||
            (chat.type === 'individual' && chat.participant?.firstName && chat.participant?.lastName)
        );
        return filtered.filter(chat =>
            (chat.participant?.firstName || chat.team?.name || '')
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        );
    }, [chats, searchTerm]);

    // Pagination logic for chat list - now uses dynamic contactsPerPage
    const totalChatPages = Math.ceil((filteredChats?.length || 0) / contactsPerPage);
    const paginatedChats = useMemo(() => {
        if (!filteredChats) return [];
        const startIndex = currentPage * contactsPerPage;
        return filteredChats.slice(startIndex, startIndex + contactsPerPage);
    }, [filteredChats, currentPage, contactsPerPage]);

    // Use the appropriate total pages based on current view
    const currentTotalPages = isCreatingNewChat ? totalPages : totalChatPages;
    
    const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, currentTotalPages - 1));
    const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 0));
    
    // Reset page when changing tabs, search, or view mode, AND when screen size changes
    useEffect(() => { 
        setCurrentPage(0); 
    }, [activeTab, searchTerm, isCreatingNewChat, contactsPerPage]);

    const handleSelectChat = (chat) => {
        const uniqueId = `${chat.type}-${chat.id}`;
        setSelectedChat({ ...chat, uniqueId });
        setChatToGlowId(null);
        if (chat.unreadCount > 0) {
            queryClient.setQueryData(['chats', user.id], (oldData) =>
                oldData?.map(c => c.id === chat.id && c.type === chat.type ? { ...c, unreadCount: 0 } : c) || []
            );
        }
    };

    const handleSelectContact = (contact) => {
        const existingChat = chats?.find(c =>
            c.id === (contact.type === 'team' ? contact.id : contact.id) &&
            c.type === contact.type
        );
        if (existingChat) {
            const uniqueId = `${existingChat.type}-${existingChat.id}`;
            setSelectedChat({ ...existingChat, uniqueId });
        } else {
            const chatObject = {
                id: contact.id,
                type: contact.type,
                participant: contact.type === 'individual' ? {
                    id: contact.id,
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    imageUrl: contact.imageUrl,
                    email: contact.email,
                    status: contact.status || 'Offline',
                    lastSeen: contact.lastSeen,
                } : null,
                team: contact.type === 'team' ? contact : null,
                lastMessage: null,
                unreadCount: 0,
                isNew: true,
                uniqueId: `${contact.type}-${contact.id}`,
            };
            setSelectedChat(chatObject);
        }
        setIsCreatingNewChat(false);
        setSearchTerm("");
    };

    // Categorized chats for students (using paginated data)
    const { mentorChat, teamChat, individualChats } = useMemo(() => {
        if (!paginatedChats || !user || user.role === 'mentor') {
            return { mentorChat: null, teamChat: null, individualChats: paginatedChats || [] };
        }
        
        // Filter chats to ensure they have complete data
        const validChats = paginatedChats.filter(chat => {
            if (chat.type === 'individual' && chat.participant) {
                return chat.participant.firstName && chat.participant.lastName;
            }
            if (chat.type === 'team' && chat.team) {
                return chat.team.name;
            }
            return false;
        });
        
        const mentor = validChats.find(c => c.type === 'individual' && c.participant?.id === user.mentorId);
        const team = validChats.find(c => c.type === 'team');
        const individuals = validChats.filter(c => c.type === 'individual' && c.participant?.id !== user.mentorId);
        
        return { mentorChat: mentor, teamChat: team, individualChats: individuals };
    }, [paginatedChats, user]);

    // NEW Fallback for mentors with no students
    const renderMentorNoChatsFallback = () => (
        <div className="flex h-full flex-col items-center justify-center text-center">
            <FaUserFriends className="text-7xl text-gray-300 dark:text-gray-600" />
            <p className="mt-6 text-xl font-semibold text-gray-600 dark:text-gray-400">You have no students yet</p>
            <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                Create classrooms and add students to start conversations.
            </p>
            <button
                onClick={() => navigate('/admin/classroom')}
                className="mt-6 px-6 py-3 rounded-full bg-brand-500 text-white font-medium transition-colors duration-200 transform hover:scale-105"
            >
                Go to Classrooms
            </button>
        </div>
    );

    const renderChatList = () => {
        if (isChatsLoading) {
            return <DottedLoader text="Loading chats..." />;
        }
        
        // This is the combined logic for mentors with no chats
        if (user?.role === 'mentor' && filteredChats.length === 0) {
            return renderMentorNoChatsFallback();
        }

        // Filter out chats with incomplete data from paginated results
        const validChats = paginatedChats.filter(chat => {
            if (chat.type === 'individual' && chat.participant) {
                return chat.participant.firstName && chat.participant.lastName;
            }
            if (chat.type === 'team' && chat.team) {
                return chat.team.name;
            }
            return false;
        });

        if (filteredChats.length === 0) {
            return (
                <div className="flex h-full flex-col items-center justify-center text-center p-4">
                    <FaComments className="text-7xl text-gray-300 dark:text-gray-600" />
                    <p className="mt-6 text-xl font-semibold text-gray-600 dark:text-gray-400">No conversations available</p>
                    <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
                        {user?.role === 'mentor' 
                            ? 'You have no students assigned yet.' 
                            : 'Start a conversation with your mentor or teammates.'
                        }
                    </p>
                </div>
            );
        }

        // Main chat list rendering with pagination controls
        return (
            <div className="flex flex-col h-full">
                <div className="flex-grow overflow-y-auto">
                    <div className="flex flex-col gap-2 p-1">
                        {validChats.length > 0 ? (
                            user?.role === 'mentor' ? (
                                validChats.map((chat) => (
                                    <div key={`${chat.type}-${chat.id}`} onClick={() => handleSelectChat(chat)}>
                                        <Chat
                                            chatData={{ ...chat, currentUserId: user.id, uniqueId: `${chat.type}-${chat.id}` }}
                                            isSelected={selectedChat?.uniqueId === `${chat.type}-${chat.id}`}
                                            shouldGlow={chatToGlowId === `${chat.type}-${chat.id}`}
                                        />
                                    </div>
                                ))
                            ) : (
                                <>
                                    {mentorChat && (
                                        <div>
                                            <h3 className="px-3 pt-4 pb-2 text-xs font-bold uppercase text-gray-400">Mentor</h3>
                                            <div className='pb-2' onClick={() => handleSelectChat(mentorChat)}>
                                                <Chat
                                                    chatData={{ ...mentorChat, currentUserId: user.id, isMentorChat: true, uniqueId: `${mentorChat.type}-${mentorChat.id}` }}
                                                    isSelected={selectedChat?.uniqueId === `${mentorChat.type}-${mentorChat.id}`}
                                                    shouldGlow={chatToGlowId === `${mentorChat.type}-${mentorChat.id}`}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {teamChat && (
                                       <div>
                                            <h3 className="px-3 pt-4 pb-2 text-xs font-bold uppercase text-gray-400">Team Chat</h3>
                                            <div className='pb-2' onClick={() => handleSelectChat(teamChat)}>
                                                <Chat
                                                    chatData={{ ...teamChat, currentUserId: user.id, uniqueId: `${teamChat.type}-${teamChat.id}` }}
                                                    isSelected={selectedChat?.uniqueId === `${teamChat.type}-${teamChat.id}`}
                                                    shouldGlow={chatToGlowId === `${teamChat.type}-${teamChat.id}`}
                                                />
                                            </div>
                                       </div>
                                    )}
                                    {individualChats.length > 0 && (
                                        <div>
                                            <h3 className="px-3 pt-4 pb-2 text-xs font-bold uppercase text-gray-400">Teammates</h3>
                                            {individualChats.map((chat) => 
                                                <div className='pb-2' key={chat.id} onClick={() => handleSelectChat(chat)}>
                                                    <Chat
                                                        chatData={{ ...chat, currentUserId: user.id, uniqueId: `${chat.type}-${chat.id}` }}
                                                        isSelected={selectedChat?.uniqueId === `${chat.type}-${chat.id}`}
                                                        shouldGlow={chatToGlowId === `${chat.type}-${chat.id}`}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )
                        ) : (
                            <div className="flex h-full flex-col items-center justify-center text-center p-4">
                                <FaComments className="text-7xl text-gray-300 dark:text-gray-600" />
                                <p className="mt-6 text-xl font-semibold text-gray-600 dark:text-gray-400">No conversations found</p>
                                <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">Try adjusting your search terms.</p>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Pagination controls for chat list */}
                {totalChatPages > 1 && (
                    <div className="flex-shrink-0 flex items-center justify-center gap-4 pt-4">
                        <button 
                            onClick={handlePrevPage} 
                            disabled={currentPage === 0} 
                            className="rounded-full p-2 text-navy-700 disabled:opacity-40 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-600"
                        >
                            <FaChevronLeft />
                        </button>
                        <span className="text-sm font-semibold text-gray-500">
                            {currentPage + 1} / {totalChatPages}
                        </span>
                        <button 
                            onClick={handleNextPage} 
                            disabled={currentPage === totalChatPages - 1} 
                            className="rounded-full p-2 text-navy-700 disabled:opacity-40 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-600"
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderNewChat = () => (
        <div className="flex h-full flex-col">
            {user?.role === 'mentor' && (
                <div className="mb-4 flex rounded-lg bg-gray-100 p-1 dark:bg-navy-900">
                    <button onClick={() => setActiveTab('teams')} className={`w-1/2 rounded-md py-2 text-sm font-semibold transition-colors ${activeTab === 'teams' ? 'bg-white text-brand-500 shadow-md dark:bg-navy-700' : 'text-gray-500'}`}>Teams</button>
                    <button onClick={() => setActiveTab('users')} className={`w-1/2 rounded-md py-2 text-sm font-semibold transition-colors ${activeTab === 'users' ? 'bg-white text-brand-500 shadow-md dark:bg-navy-700' : 'text-gray-500'}`}>Users</button>
                </div>
            )}
            <div className="flex-grow space-y-3">
                {areContactsLoading ? <DottedLoader text="Loading contacts..." /> : paginatedContacts.map(contact => (
                    <div key={`${contact.type}-${contact.id}`} onClick={() => handleSelectContact(contact)} className="flex cursor-pointer items-center gap-4 rounded-lg p-3 transition-colors hover:bg-gray-100 dark:hover:bg-navy-700">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${contact.type === 'team' ? 'bg-indigo-100 dark:bg-indigo-800' : 'bg-green-100 dark:bg-green-800'}`}>
                            {contact.type === 'team' ? <BsFillPeopleFill className="h-5 w-5 text-indigo-500" /> : <BsPersonFill className="h-5 w-5 text-green-500" />}
                        </div>
                        <p className="font-semibold text-navy-700 dark:text-white">{contact.name || `${contact.firstName} ${contact.lastName}`}</p>
                    </div>
                ))}
                {paginatedContacts.length === 0 && !areContactsLoading && <p className="py-10 text-center text-gray-500">No contacts found.</p>}
            </div>
            {totalPages > 1 && (
                <div className="mt-auto flex-shrink-0 flex items-center justify-center gap-4 pt-4">
                    <button onClick={handlePrevPage} disabled={currentPage === 0} className="rounded-full p-2 text-navy-700 disabled:opacity-40 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-600"><FaChevronLeft /></button>
                    <span className="text-sm font-semibold text-gray-500">{currentPage + 1} / {totalPages}</span>
                    <button onClick={handleNextPage} disabled={currentPage === totalPages - 1} className="rounded-full p-2 text-navy-700 disabled:opacity-40 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-600"><FaChevronRight /></button>
                </div>
            )}
        </div>
    );
    
    return (
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3 h-[calc(100vh-120px)] pt-[50px] lg:pt-[5px]">
            {/* Remove team restriction - allow all users to access community */}
            <>
                {(!isMobile || (isMobile && !selectedChat)) && (
                    <div className="h-full lg:col-span-1">
                        <Card extra={"w-full h-full p-4 flex flex-col bg-gray-100 dark:bg-navy-800"}>
                            <div className="mb-4 flex items-center justify-between">
                                <div>
                                    <h4 className="text-2xl font-bold text-navy-700 dark:text-white">{isCreatingNewChat ? "New Message" : "Messages"}</h4>
                                    <p className="text-sm text-gray-500">{isCreatingNewChat ? "Select a contact to begin" : "Your recent conversations"}</p>
                                </div>
                                <button onClick={() => setIsCreatingNewChat(c => !c)} className={`flex h-10 w-10 items-center justify-center rounded-full text-white shadow-lg transition-all duration-300 ${isCreatingNewChat ? 'bg-red-500 rotate-[225deg]' : 'bg-brand-500 hover:scale-110'}`}>
                                    <BsPencilSquare size={18} />
                                </button>
                            </div>
                            <div className="relative mb-4">
                                <FiSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                                <input type="text" placeholder={isCreatingNewChat ? "Search contacts..." : "Search chats..."} className="w-full rounded-full bg-gray-100 py-3 pl-12 pr-4 text-sm font-medium outline-none dark:bg-navy-900" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </div>
                            <div className="flex-grow overflow-y-auto pr-2">
                                {isCreatingNewChat ? renderNewChat() : renderChatList()}
                            </div>
                        </Card>
                    </div>
                )}

                {(!isMobile || (isMobile && selectedChat)) && (
                    <div className="h-full lg:col-span-2">
                        {selectedChat ? (
                            <SingleChat key={selectedChat.uniqueId} chat={selectedChat} onClose={() => setSelectedChat(null)} isMobile={isMobile} />
                        ) : (
                            <div className="flex h-full flex-col items-center justify-center rounded-2xl bg-gray-50 dark:bg-navy-800">
                                <FaComments className="text-7xl text-gray-300 dark:text-gray-600" />
                                <p className="mt-6 text-xl font-semibold text-gray-600 dark:text-gray-400">Select a chat to view messages</p>
                                <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">Your conversations will appear here.</p>
                            </div>
                        )}
                    </div>
                )}
            </>
        </div>
    );
};

export default Messages;